import styles from "./index.module.scss";
import { NextPage } from "next";
import StockOptions from "@/components/stock-options/StockOptions";
import OptionsPlatformOfferHost from "@/components/options-platform/offer-host/OptionsPlatformOfferHost";
import { Stock } from "@/models/stock";
import Head from "next/head";
import { buildStockOgImage } from "@/lib/stock-og";
import { useSetAtom } from "jotai";
import { stockTradingItemIdState } from "@/lib/store";
import { useEffect } from "react";
import { useLegacyOptionsVisible, useOptionsUiRedirect } from "src/hooks/useOptionsUiRedirect";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { stockDescriptions, stockTitle } from "@/lib/stock-seo";
import { stockCanonicalUrl, stockPath } from "@/lib/stock-url";
import { isUsExchange } from "@/lib/vars";

const StockOptionsPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    // Users entitled to the new platform are forwarded there.
    useOptionsUiRedirect("legacy", stock ? stockPath(stock, "options-platform") : null);
    // …and must not glimpse the old interface while that happens: the
    // redirect can only fire once the user payload lands, so hold the
    // legacy content back until we know this user belongs here.
    const legacyVisible = useLegacyOptionsVisible();
    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);
    return (
        <>
            {stock &&
                (() => {
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "Options");
                    return (
                        <Head>
                            <title>{stockTitle(stock, "options")}</title>
                            <link rel="canonical" href={stockCanonicalUrl(stock, "options")} />
                            <meta name="keywords" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, Options, Stock Options, Interesting Option Flow, Option Sentiment, Option Chain`} />
                            <meta name="description" content={stockDescriptions.options(stock)} />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content={stockTitle(stock, "options")} />
                            <meta name="twitter:description" content={stockDescriptions.options(stock)} />
                            <meta name="twitter:url" content={stockCanonicalUrl(stock, "options")} />
                            <meta name="twitter:image" content={ogImageUrl} />
                            <meta name="twitter:image:alt" content={cardTitle} />
                            <meta property="og:title" content={stockTitle(stock, "options")} />
                            <meta property="og:description" content={stockDescriptions.options(stock)} />
                            <meta property="og:url" content={stockCanonicalUrl(stock, "options")} />
                            <meta property="og:image" content={ogImageUrl} key="og:image" />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                            <meta property="og:image:alt" content={cardTitle} />
                        </Head>
                    );
                })()}
            <div className={styles.StockOptionsPage}>
                {legacyVisible && (stock ? <StockOptions stock={stock} /> : <h1>Stock not found.</h1>)}
                {legacyVisible && <OptionsPlatformOfferHost />}
            </div>
        </>
    );
};

// Options data is OPRA-licensed and US-only. This page once forwarded those stocks to the platform,
// which sent them back here, so platform users got a blank page.
export const getServerSideProps = stockPageGetServerSideProps({ tab: "options", allowedTypes: [1, 3], redirectTab: (stock) => (isUsExchange(stock.exchangesymbol) ? null : "") });

export default StockOptionsPage;
